import React, { useState, useEffect, useRef, useCallback } from 'react';
import { generateId } from '../api/mockApi'; 
import Sidebar from './Sidebar';
import CenterPanel from './CenterPanel';
import RightPanel from './RightPanel';
import Modal from './Modal';
import PaperDetailModal from './PaperDetailModal'; 
import TaxonomyNodeDetailModal from './TaxonomyNodeDetailModal';
import { X } from 'lucide-react'; 

// API Config
const API_URL = '/api';
//const API_URL = 'http://localhost:5002/api'; 
const getToken = () => localStorage.getItem('synapseToken');

const authenticatedFetch = async (url, options = {}) => {
    const token = getToken();
    const headers = { ...options.headers }; 

    if (!(options.body instanceof FormData)) { 
        headers['Content-Type'] = 'application/json';
    }

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, { ...options, headers });
    
    let data;
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
        data = await response.json();
    } else {
        data = response.status === 204 ? {} : { message: await response.text() };
    }

    if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }
    return data;
};

function MainAppPage({ currentUser, userId, onLogout }) {
    // --- State Management ---
    const [viewMode, setViewMode] = useState('chat');
    const [knowledgeBases, setKnowledgeBases] = useState([]);
    const [currentKB, setCurrentKB] = useState(null);
    const [kbPapers, setKbPapers] = useState([]); 
    const [chatSessions, setChatSessions] = useState([]);
    const [currentSession, setCurrentSession] = useState(null);
    const [messages, setMessages] = useState([]);
    
    // UI Layout State
    const [isRightPanelOpen, setIsRightPanelOpen] = useState(true);

    // Search & Progress
    const [searchResults, setSearchResults] = useState([]); 
    const [chatInput, setChatInput] = useState('');
    const [searchProgress, setSearchProgress] = useState({ status: 'idle', progressData: null });
    
    // Task Progress
    const [activeTask, setActiveTask] = useState({ taskId: null, type: null, status: 'idle', steps: [] });
    const [pendingTask, setPendingTask] = useState({ taskId: null, type: null });

    // Selected Papers
    const [selectedPaperIds, setSelectedPaperIds] = useState(new Set());

    // UI & Modals
    const [showCreateKbModal, setShowCreateKbModal] = useState(false);
    const [newKbName, setNewKbName] = useState('');
    const [genericModal, setGenericModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null, showCancel: false });
    const [loading, setLoading] = useState({ kbs: false, sessions: false, messages: false, papers: false, action: false, profile: false, upload: false });
    
    // Profiles
    const [selectedPaperProfile, setSelectedPaperProfile] = useState(null);
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

    const fileInputRef = useRef(null); 
    const progressPollInterval = useRef(null);
    const taskPollInterval = useRef(null);

    const [sidebarViewMode, setSidebarViewMode] = useState('papers');
    const [problemTaxonomyData, setProblemTaxonomyData] = useState(null);
    const [methodTaxonomyData, setMethodTaxonomyData] = useState(null);
    const [loadingTaxonomy, setLoadingTaxonomy] = useState(false);
    const [selectedProblemIds, setSelectedProblemIds] = useState(new Set());
    const [selectedMethodIds, setSelectedMethodIds] = useState(new Set());

    // Node Profile
    const [selectedNodeProfile, setSelectedNodeProfile] = useState(null);
    const [isNodeProfileModalOpen, setIsNodeProfileModalOpen] = useState(false);
    const [loadingNodeProfile, setLoadingNodeProfile] = useState(false);

    const handleShowNodeProfile = async (node, type) => {
        if (!userId || !currentKB || !node) return;
        setLoadingNodeProfile(true);
        setIsNodeProfileModalOpen(true);
        setSelectedNodeProfile(null); 
        try {
            const data = await authenticatedFetch(
                `${API_URL}/users/${userId}/kbs/${currentKB.id}/taxonomy/${type}/nodes/${node.id}`
            );
            setSelectedNodeProfile({ 
                ...data, 
                name: node.name, 
                id: node.id,
                taxonomyType: type 
            });
        } catch (e) {
            console.error("Node profile fetch error:", e);
            setSelectedNodeProfile({
                name: node.name,
                id: node.id,
                taxonomyType: type,
                description: "Failed to load details."
            });
        } finally {
            setLoadingNodeProfile(false);
        }
    };

    // --- Handlers ---
    const handleToggleProblemSelection = useCallback((id) => {
        setSelectedProblemIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) newSet.delete(id);
            else newSet.add(id);
            return newSet;
        });
    }, []);

    const handleToggleMethodSelection = useCallback((id) => {
        setSelectedMethodIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) newSet.delete(id);
            else newSet.add(id);
            return newSet;
        });
    }, []);

    const fetchTaxonomy = useCallback(async (type) => {
        if (!userId || !currentKB) return;
        setLoadingTaxonomy(true);
        try {
            const data = await authenticatedFetch(
                `${API_URL}/users/${userId}/kbs/${currentKB.id}/taxonomy/${type}`
            );
            if (type === 'problem') setProblemTaxonomyData(data);
            if (type === 'method') setMethodTaxonomyData(data);
        } catch (e) {
            if (type === 'problem') setProblemTaxonomyData(null);
            if (type === 'method') setMethodTaxonomyData(null);
        } finally {
            setLoadingTaxonomy(false);
        }
    }, [userId, currentKB]);

    useEffect(() => {
        if (!currentKB) {
            setProblemTaxonomyData(null);
            setMethodTaxonomyData(null);
            return;
        }
        if (sidebarViewMode === 'problems') fetchTaxonomy('problem');
        else if (sidebarViewMode === 'methods') fetchTaxonomy('method');
    }, [currentKB, sidebarViewMode, fetchTaxonomy]);

    const showGenericModal = (title, message, onConfirm, showCancel = false) => {
        setGenericModal({ isOpen: true, title, message, onConfirm, showCancel });
    };
    const closeGenericModal = () => setGenericModal({ ...genericModal, isOpen: false });

    const handleTogglePaperSelection = useCallback((paperId) => {
        setSelectedPaperIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(paperId)) newSet.delete(paperId);
            else newSet.add(paperId);
            return newSet;
        });
    }, []);

    const handleClearPaperSelection = useCallback(() => setSelectedPaperIds(new Set()), []);
    useEffect(() => setSelectedPaperIds(new Set()), [currentKB]);

    const fetchKBs = useCallback(async () => {
        if (!userId) return;
        setLoading(prev => ({ ...prev, kbs: true }));
        try {
            const data = await authenticatedFetch(`${API_URL}/users/${userId}/kbs`);
            setKnowledgeBases(data);
            if (!currentKB && data.length > 0) {
                const def = data.find(kb => kb.isDefault) || data[0];
                setCurrentKB(def);
            }
        } catch (e) { console.error(e); }
        finally { setLoading(prev => ({ ...prev, kbs: false })); }
    }, [userId, currentKB]);

    const fetchKBPapers = useCallback(async (kbId) => {
        if (!userId || !kbId) return;
        try {
            const data = await authenticatedFetch(`${API_URL}/users/${userId}/kbs/${kbId}/papers`);
            const mapped = data.map(p => ({
                id: p.id, title: p.title, abstract: p.abstract, source: p.source, addedAt: p.added_at
            }));
            setKbPapers(mapped);
        } catch (e) { console.error(e); }
    }, [userId]);

    const fetchSessions = useCallback(async () => {
        if (!userId) return;
        setLoading(prev => ({ ...prev, sessions: true }));
        try {
            const data = await authenticatedFetch(`${API_URL}/users/${userId}/sessions`);
            data.sort((a, b) => new Date(b.last_message_at || b.created_at) - new Date(a.last_message_at || a.created_at));
            const mapped = data.map(s => ({
                id: s.id, name: s.name, kbIdUsed: s.kb_id_used, kbNameUsed: s.kb_name_used
            }));
            setChatSessions(mapped);
        } catch (e) { console.error(e); }
        finally { setLoading(prev => ({ ...prev, sessions: false })); }
    }, [userId]);

    const fetchMessages = useCallback(async (sessionId) => {
        if (!userId || !sessionId) return;
        try {
            const data = await authenticatedFetch(`${API_URL}/users/${userId}/${sessionId}/messages`);
            setMessages(data);
        } catch (e) { console.error(e); }
    }, [userId]);

    useEffect(() => { fetchKBs(); fetchSessions(); }, [fetchKBs, fetchSessions]);
    useEffect(() => { if (currentKB) fetchKBPapers(currentKB.id); }, [currentKB, fetchKBPapers]);
    useEffect(() => { if (currentSession) fetchMessages(currentSession.id); else setMessages([]); }, [currentSession, fetchMessages]);

    useEffect(() => {
        if (!activeTask.taskId) {
            if (taskPollInterval.current) {
                clearInterval(taskPollInterval.current);
                taskPollInterval.current = null;
            }
            return;
        }

        const pollTask = async () => {
            if (!activeTask.taskId) return;
            try {
                const res = await fetch(`${API_URL}/tasks/${activeTask.taskId}/status`);
                if (!res.ok) return; 
                const data = await res.json();
                setActiveTask(prev => ({
                    ...prev,
                    steps: data.steps,
                    status: data.is_finished ? 'completed' : 'processing'
                }));

                if (data.is_finished) {
                    if (activeTask.type === 'query_planning') {
                        if (pendingTask.taskId) {
                            setActiveTask({
                                taskId: pendingTask.taskId,
                                type: pendingTask.type, 
                                status: 'processing',
                                steps: [] 
                            });
                            setPendingTask({ taskId: null, type: null }); 
                        } else {
                            clearInterval(taskPollInterval.current);
                            taskPollInterval.current = null;
                        }
                        return;
                    }
                    
                    if (activeTask.type === 'upload') {
                        setLoading(prev => ({ ...prev, upload: false }));
                        if (currentKB) fetchKBPapers(currentKB.id);
                        showGenericModal("Success", "File processed successfully.");
                    } else if (activeTask.type === 'query_processing') {
                        setLoading(prev => ({ ...prev, action: false }));
                        if (currentSession) {
                            setTimeout(() => fetchMessages(currentSession.id), 500); 
                        }
                    }
                    clearInterval(taskPollInterval.current);
                    taskPollInterval.current = null;
                    setTimeout(() => setActiveTask({ taskId: null, type: null, status: 'idle', steps: [] }), 5000);
                }
            } catch (e) { console.error("Task poll error:", e); }
        };

        if (!taskPollInterval.current) {
            taskPollInterval.current = setInterval(pollTask, 1500); 
        }

        return () => {
            if (taskPollInterval.current) {
                clearInterval(taskPollInterval.current);
                taskPollInterval.current = null;
            }
        };
    }, [activeTask.taskId, activeTask.type, pendingTask, currentKB, fetchKBPapers, currentSession, fetchMessages]);

    const handleCreateKB = async () => {
        try {
            await authenticatedFetch(`${API_URL}/users/${userId}/kbs`, { method: 'POST', body: JSON.stringify({ name: newKbName }) });
            setNewKbName('');
            setShowCreateKbModal(false);
            fetchKBs();
            showGenericModal("Success", "Knowledge Base created successfully.");
        } catch (e) { showGenericModal("Error", e.message); }
    };

    const handleDeletePaper = (kbId, paperId, title) => {
        showGenericModal("Delete Paper", `Remove "${title}" from KB?`, async () => {
            try {
                await authenticatedFetch(`${API_URL}/users/${userId}/kbs/${kbId}/papers/${paperId}`, { method: 'DELETE' });
                fetchKBPapers(kbId);
                closeGenericModal();
            } catch (e) { console.error(e); }
        }, true);
    };

    const handleNewChat = async () => {
        if (!currentKB) return showGenericModal("Notice", "Select a KB first.");
        setCurrentSession(null); 
        setMessages([]);
        setViewMode('chat');
    };

    const handleCreateTaxonomy = async () => {
        if (!currentKB) return showGenericModal("Action Required", "Please select a Knowledge Base first.");
        if (kbPapers.length === 0) return showGenericModal("Notice", "This KB is empty. Add papers before creating a taxonomy.");
        try {
            const response = await authenticatedFetch(
                `${API_URL}/users/${userId}/kbs/${currentKB.id}/taxonomy`,
                { method: 'POST' }
            );
            if (response.task_id) {
                setActiveTask({
                    taskId: response.task_id,
                    type: 'taxonomy',
                    status: 'processing',
                    steps: [] 
                });
            }
        } catch (e) {
            showGenericModal("Error", "Failed to create taxonomy: " + e.message);
        }
    };

    const handleShowPaperProfile = async (paper) => {
        if (!userId || !currentKB || !paper) return;
        setLoading(prev => ({ ...prev, profile: true }));
        setIsProfileModalOpen(true);
        setSelectedPaperProfile(null); 
        try {
            const data = await authenticatedFetch(
                `${API_URL}/users/${userId}/kbs/${currentKB.id}/papers/${paper.id}/profile`
            );
            setSelectedPaperProfile(data);
        } catch (e) {
            showGenericModal("Error", "Failed to load paper profile: " + e.message);
            setIsProfileModalOpen(false);
        } finally {
            setLoading(prev => ({ ...prev, profile: false }));
        }
    };

    const handleSendMessage = async () => {
        if (!userId || !chatInput.trim() || !currentKB) return;
        setLoading(prev => ({ ...prev, action: true }));
        
        const tempMsg = { id: generateId(), text: chatInput, sender: 'user', timestamp: new Date().toISOString() };
        setMessages(prev => [...prev, tempMsg]);
        const msgToSend = chatInput;
        setChatInput('');

        const paperList = Array.from(selectedPaperIds);
        const problemList = Array.from(selectedProblemIds).map(id => `problem-${id}`);
        const methodList = Array.from(selectedMethodIds).map(id => `method-${id}`);
        const allContextIds = [...paperList, ...problemList, ...methodList];

        try {
            const sessionId = currentSession ? currentSession.id : `session_${generateId()}`;
            const response = await authenticatedFetch(
                `${API_URL}/chat/${sessionId}/users/${userId}/kbs/${currentKB.id}`, 
                { 
                    method: 'POST', 
                    body: JSON.stringify({ 
                        message: msgToSend,
                        paper_ids: allContextIds 
                    }) 
                }
            );

            if (!currentSession || currentSession.id !== response.chat_session.id) {
                const newSess = {
                    id: response.chat_session.id,
                    name: response.chat_session.name,
                    kbNameUsed: currentKB.name
                };
                setCurrentSession(newSess);
                setChatSessions(prev => [newSess, ...prev]);
            }

            if (response.planning_task_id && response.processing_task_id) {
                setActiveTask({
                    taskId: response.planning_task_id,
                    type: 'query_planning',
                    status: 'processing',
                    steps: [] 
                });
                setPendingTask({
                    taskId: response.processing_task_id,
                    type: 'query_processing'
                });
            } else {
                setLoading(prev => ({ ...prev, action: false }));
            }
        } catch (e) {
            setMessages(prev => prev.filter(m => m.id !== tempMsg.id)); 
            showGenericModal("Error", "Failed to send message: " + e.message);
            setLoading(prev => ({ ...prev, action: false }));
        }
    };

    const pollSearchStatus = async () => {
        try {
            const res = await fetch(`${API_URL}/search_status`); 
            const status = await res.json();
            if (status.current_step) {
                setSearchProgress({
                    status: status.current_step === 'idle' ? 'idle' : 
                            status.current_step === 'completed' ? 'completed' : 'processing',
                    progressData: {
                        steps: status.steps || [],
                        resultsCount: status.results_count || 0
                    }
                });
            }
        } catch (e) { console.warn("Polling error", e); }
    };

    const handlePaperSearch = async (seedPapers = null) => {
        if (!chatInput.trim()) return;
        setViewMode('paperSearchResults');
        setLoading(prev => ({ ...prev, papers: true }));
        setSearchProgress({ status: 'processing', progressData: { steps: [] } });
        progressPollInterval.current = setInterval(pollSearchStatus, 1500);

        try {
            const results = await authenticatedFetch(`${API_URL}/paper_search`, {
                method: 'POST',
                body: JSON.stringify({ message: chatInput.trim() })
            });
            setSearchResults(results);
            setSearchProgress(prev => ({ ...prev, status: 'completed' }));
        } catch (e) {
            showGenericModal("Search Failed", e.message);
            setSearchProgress(prev => ({ ...prev, status: 'failed' }));
        } finally {
            clearInterval(progressPollInterval.current);
            setLoading(prev => ({ ...prev, papers: false }));
        }
    };

    const handleTriggerKBFileUpload = () => fileInputRef.current && fileInputRef.current.click();
    
    const handleFileSelected = async (e) => {
        const file = e.target.files[0];
        if (!file || !currentKB) return;
        
        const formData = new FormData();
        formData.append('file', file);
        setLoading(prev => ({ ...prev, upload: true })); 

        try {
            const response = await authenticatedFetch(
                `${API_URL}/users/${userId}/kbs/${currentKB.id}/upload_file_for_processing`,
                { method: 'POST', body: formData }
            );
            if (response.task_id) {
                setActiveTask({
                    taskId: response.task_id,
                    type: 'upload',
                    status: 'processing',
                    steps: [] 
                });
            } else {
                showGenericModal("Success", "File uploaded (No tracking ID returned).");
                fetchKBPapers(currentKB.id); 
                setLoading(prev => ({ ...prev, upload: false }));
            }
        } catch (err) { 
            showGenericModal("Error", err.message); 
            setLoading(prev => ({ ...prev, upload: false }));
        }
    };
    
    const handleAddPaperToKB = async (paper) => {
        if(!currentKB) return;
        try {
            await authenticatedFetch(`${API_URL}/users/${userId}/kbs/${currentKB.id}/upload_paper`, {
                method: 'POST',
                body: JSON.stringify({ paper })
            });
            showGenericModal("Success", "Paper added to KB.");
            fetchKBPapers(currentKB.id);
        } catch (e) { showGenericModal("Error", e.message); }
    };

    // --- Render ---
    return (
        <div className="static-shell" style={{ 
            display: 'grid',
            gridTemplateColumns: isRightPanelOpen ? '320px 1fr 360px' : '320px 1fr',
            height: '100vh',
            background: 'var(--bg-primary)'
        }}>
            {/* 1. Left Sidebar */}
            <Sidebar 
                currentUser={currentUser}
                userId={userId}
                onLogout={onLogout}
                knowledgeBases={knowledgeBases}
                currentKB={currentKB}
                papersInView={kbPapers}
                handleSelectActiveKBForOperations={(kb) => setCurrentKB(kb)}
                handleDeletePaper={handleDeletePaper}
                setShowCreateKbModal={setShowCreateKbModal}
                handleTriggerKBFileUpload={handleTriggerKBFileUpload}
                handleCreateTaxonomy={handleCreateTaxonomy}
                handleShowPaperProfile={handleShowPaperProfile} 
                handlePaperSearchSetup={() => {
                    setViewMode('paperSearchResults');
                    setChatInput('');
                }}
                selectedPaperIds={selectedPaperIds} 
                handleTogglePaperSelection={handleTogglePaperSelection}
                handleClearPaperSelection={handleClearPaperSelection} 
                isUploading={loading.upload} 
                viewMode={sidebarViewMode}
                setViewMode={setSidebarViewMode}
                problemTaxonomyData={problemTaxonomyData}
                methodTaxonomyData={methodTaxonomyData}
                loadingTaxonomy={loadingTaxonomy}
                selectedProblemIds={selectedProblemIds}
                handleToggleProblemSelection={handleToggleProblemSelection}
                selectedMethodIds={selectedMethodIds}
                handleToggleMethodSelection={handleToggleMethodSelection}
                handleShowNodeProfile={handleShowNodeProfile}
            />

            {/* 2. Center Workspace */}
            <CenterPanel 
                viewMode={viewMode}
                messages={messages}
                papersInView={searchResults}
                loading={loading}
                chatInput={chatInput}
                setChatInput={setChatInput}
                handleSendMessage={handleSendMessage}
                handlePaperSearch={handlePaperSearch}
                onAddToKB={handleAddPaperToKB}
                currentKB={currentKB}
                isRightPanelOpen={isRightPanelOpen}
                onToggleRightPanel={() => setIsRightPanelOpen(!isRightPanelOpen)}
            />

            {/* 3. Right Progress/Session Panel */}
            {isRightPanelOpen && (
                <RightPanel 
                    chatSessions={chatSessions}
                    currentSession={currentSession}
                    selectChatSession={(s) => {
                        setCurrentSession(s);
                        setViewMode('chat');
                        const kbUsed = knowledgeBases.find(k => k.id === s.kbIdUsed);
                        if(kbUsed) setCurrentKB(kbUsed);
                    }}
                    handleNewChat={handleNewChat}
                    searchProgress={searchProgress}
                    activeTask={activeTask} 
                />
            )}

            {/* Modals & Inputs */}
            <Modal 
                showCreateKbModal={showCreateKbModal} 
                setShowCreateKbModal={setShowCreateKbModal}
                newKbName={newKbName}
                setNewKbName={setNewKbName}
                handleCreateKB={handleCreateKB}
                loading={loading.kbs}
            />

            <PaperDetailModal
                isOpen={isProfileModalOpen}
                onClose={() => setIsProfileModalOpen(false)}
                paperData={selectedPaperProfile}
                loading={loading.profile}
            />

            <TaxonomyNodeDetailModal
                isOpen={isNodeProfileModalOpen}
                onClose={() => setIsNodeProfileModalOpen(false)}
                nodeData={selectedNodeProfile}
                loading={loadingNodeProfile}
            />

            {genericModal.isOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000]">
                    <div className="bg-white p-6 rounded-lg shadow-xl max-w-sm w-full">
                        <h3 className="font-bold text-lg mb-2">{genericModal.title}</h3>
                        <p className="mb-4 text-gray-600">{genericModal.message}</p>
                        <div className="flex justify-end gap-2">
                            {genericModal.showCancel && (
                                <button onClick={closeGenericModal} className="px-4 py-2 border rounded">Cancel</button>
                            )}
                            <button 
                                onClick={() => { 
                                    if(genericModal.onConfirm) genericModal.onConfirm(); 
                                    else closeGenericModal(); 
                                }} 
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <input 
                type="file" 
                ref={fileInputRef} 
                onChange={handleFileSelected} 
                className="hidden" 
                accept=".pdf,.txt,.md,.docx"
            />
        </div>
    );
}

export default MainAppPage;
